/**
 * Carrier plans as the server last described them.
 *
 * Loaded once over REST when a screen that shows carriers mounts, then kept
 * current by `carrier` frames on the telemetry socket. There is no timer: the
 * executor pushes every launch, landing and recovery as it happens, and a
 * poll layered on top would only ever be able to overwrite a fresher frame
 * with an older answer, which is the predecessor's 2-second-poll bug again.
 */

import { useMemo } from 'react';
import { create } from 'zustand';

import { listCarrierPlans, type CarrierPhase, type CarrierPlan } from '@/api/carriers';
import { wsClient } from '@/api/ws';
import type { ServerMessage } from '@/types/ws';

interface CarrierStoreState {
  plans: Record<string, CarrierPlan>;
  loaded: boolean;
  load: () => Promise<void>;
  apply: (carrierId: string, plan: CarrierPlan | null) => void;
  reset: () => void;
}

export const useCarrierStore = create<CarrierStoreState>((set) => ({
  plans: {},
  loaded: false,
  load: async () => {
    const result = await listCarrierPlans();
    if (!result.ok) return;
    const plans: Record<string, CarrierPlan> = {};
    for (const plan of result.data.plans) plans[plan.carrierId] = plan;
    set({ plans, loaded: true });
  },
  apply: (carrierId, plan) =>
    set((state) => {
      const plans = { ...state.plans };
      if (plan === null) delete plans[carrierId];
      else plans[carrierId] = plan;
      return { plans };
    }),
  reset: () => set({ plans: {}, loaded: false }),
}));

/** Feed one socket frame to the store. Exported for the tests. */
export function applyCarrierFrame(message: ServerMessage): void {
  if (message.type !== 'carrier') return;
  useCarrierStore.getState().apply(message.carrierId, message.plan as CarrierPlan | null);
}

let unsubscribe: (() => void) | null = null;

/** Start listening for carrier frames. Idempotent; the first caller wins. */
export function watchCarriers(): void {
  if (unsubscribe !== null) return;
  unsubscribe = wsClient.subscribe(applyCarrierFrame);
}

const EMPTY: CarrierPlan[] = [];

export function useCarrierPlans(): CarrierPlan[] {
  const plans = useCarrierStore((state) => state.plans);
  return useMemo(() => {
    const list = Object.values(plans);
    return list.length ? list : EMPTY;
  }, [plans]);
}

export function useCarrierPlan(carrierId: string | null): CarrierPlan | null {
  return useCarrierStore((state) => (carrierId ? (state.plans[carrierId] ?? null) : null));
}

export function phaseOf(plan: CarrierPlan, droneId: string): CarrierPhase {
  return plan.status[droneId]?.phase ?? 'aboard';
}

export function aboardCount(plan: CarrierPlan): number {
  return plan.embarked.filter((entry) => phaseOf(plan, entry.droneId) === 'aboard').length;
}
